import React, { useMemo, useRef, useState } from 'react';
import { Flex, FlexProps } from 'rebass';
import { useMutation } from '@apollo/client';
import {
  convertJobAttachment,
  convertJobComment,
  convertJobStatusChange,
  JobHistoryAddress,
  JobHistoryAttachment,
} from './helpers';
import { sortHistoryItems } from '../Timeline/helpers';
import { gql } from '@/modes/individual/gql';
import Timeline from '../Timeline/Timeline';
import { GrowyTextarea } from '../Form/GrowyTextarea';
import { Checkbox, Label } from '../Form';
import { JobComment, JobStatusChange } from './queries';
import { JobHistoryItem } from './types';
import RequirePerms from '../RequirePerms';
import useMode from '@/hooks/useMode';
import useSessionInfo from '@/hooks/useSessionInfo';

const CREATE_JOB_COMMENT = gql(`
  mutation CreateJobComment($input: JobCommentInput!) {
    createJobComment(input: $input) {
      id
    }
  }
`);

type JobHistoryProps = FlexProps & {
  jobId: string;
  comments: JobComment[];
  statusChanges: JobStatusChange[];
  attachments: JobHistoryAttachment[];
  jobAddress?: JobHistoryAddress;
  refetch?: () => Promise<unknown>;
  waterSupplierName?: string | null;
  waterSupplierProfileId?: string | null;
};

type JobHistoryType = 'COMMENT' | 'STATUS' | 'ATTACHMENT';

// History state
const jobHistoryItemTypes: JobHistoryType[] = [
  'COMMENT',
  'STATUS',
  'ATTACHMENT',
];

const JobHistory: React.FC<JobHistoryProps> = ({
  comments,
  statusChanges,
  attachments,
  jobAddress,
  jobId,
  refetch,
  waterSupplierName,
  waterSupplierProfileId,
  ...props
}) => {
  const [createJobComment] = useMutation(CREATE_JOB_COMMENT, {});
  const [comment, setComment] = useState('');
  const [sendToWaterSupplier, setSendToWaterSupplier] = useState(false);
  const mode = useMode();
  const sessionInfo = useSessionInfo();
  const isWaterSupplierMode = mode === 'water-supplier';
  const visibleWaterSupplierName = !isWaterSupplierMode
    ? (waterSupplierName ?? sessionInfo.session?.companyProfile?.name)
    : undefined;

  const scrollDiv = useRef<any>();

  const matchVisibleHistoryTypes = (item: JobHistoryItem) => {
    // safety check, since not required
    if (!selectedHistoryItemTypes) {
      return true;
    }
    if (selectedHistoryItemTypes.includes('COMMENT')) {
      if (['COMMENT', 'APPROVAL'].includes(item.type)) {
        return true;
      }
    }
    if (selectedHistoryItemTypes.includes('STATUS')) {
      if (item.type === 'STATUS') {
        return true;
      }
    }
    if (selectedHistoryItemTypes.includes('ATTACHMENT')) {
      if (item.type === 'ATTACHMENT') {
        return true;
      }
    }
    return false;
  };

  const [selectedHistoryItemTypes, setSelectedHistoryItemTypes] =
    useState<JobHistoryType[]>(jobHistoryItemTypes);

  const amendedHistoryItems = useMemo<JobHistoryItem[]>(() => {
    const items: JobHistoryItem[] = [
      ...comments.map((comment) =>
        convertJobComment(
          comment,
          visibleWaterSupplierName,
          waterSupplierProfileId
        )
      ),
      ...statusChanges.map(convertJobStatusChange),
      ...attachments.map((attachment) =>
        convertJobAttachment(
          attachment,
          mode === 'company' ||
            mode === 'individual' ||
            mode === 'water-supplier',
          jobAddress
        )
      ),
    ];

    // Convert, concat, filter, sort and group
    return items.filter(matchVisibleHistoryTypes).sort(sortHistoryItems);
  }, [
    comments,
    visibleWaterSupplierName,
    waterSupplierProfileId,
    statusChanges,
    attachments,
    jobAddress,
    mode,
    selectedHistoryItemTypes,
    matchVisibleHistoryTypes,
  ]);

  const availableHistoryItemTypes = useMemo(() => {
    const available: JobHistoryType[] = [];
    if (comments.length) {
      available.push('COMMENT');
    }
    if (attachments.length) {
      available.push('ATTACHMENT');
    }
    if (statusChanges.length) {
      available.push('STATUS');
    }
    return available;
  }, [comments, attachments, statusChanges]);

  return (
    <Flex
      {...(props as any)}
      flexDirection="column"
      flexGrow={1}
      flexShrink={1}
    >
      <Timeline
        selectedHistoryItemTypes={selectedHistoryItemTypes}
        setSelectedHistoryItemTypes={setSelectedHistoryItemTypes}
        historyItemTypes={availableHistoryItemTypes}
        historyItems={amendedHistoryItems}
        scrollDivRef={scrollDiv}
      />

      {/************************************
       * Message Box
       ************************************/}
      <RequirePerms jobs="UPDATE">
        <Flex
          sx={{ borderTop: '1px solid', borderTopColor: 'tableBorder' }}
          flexDirection="column"
          flexGrow={0}
          flexShrink={0}
          p={4}
        >
          <GrowyTextarea
            height={34}
            focusedHeight={66}
            maxCharacters={1000}
            value={comment}
            onChange={(e) => {
              setComment(e.target.value);
            }}
            placeholder="Enter message"
            onKeyPress={async (e) => {
              if (e.key === 'Enter' || e.key === 'NumpadEnter') {
                e.preventDefault();
                if (comment.trim().length > 0) {
                  await createJobComment({
                    variables: {
                      input: {
                        jobId,
                        comment,
                        visibility:
                          isWaterSupplierMode || sendToWaterSupplier
                            ? 'EVERYONE'
                            : 'INTERNAL',
                      },
                    },
                  });
                  setComment('');
                  if (refetch) {
                    await refetch();
                  }
                  scrollDiv.current.scrollTop = 0;
                }
              }
            }}
          />
          {!isWaterSupplierMode && (
            <Label
              mt={2}
              sx={{
                alignItems: 'center',
                color: 'text',
                display: 'inline-flex',
                fontSize: 1,
                fontWeight: 'normal',
                gap: '5px',
              }}
            >
              <Checkbox
                variant="forms.checkbox"
                checked={sendToWaterSupplier}
                onChange={(e) => setSendToWaterSupplier(e.target.checked)}
                sx={{ mr: 0 }}
              />
              Send to water supplier
            </Label>
          )}
        </Flex>
      </RequirePerms>
    </Flex>
  );
};

export default JobHistory;
